import * as React from 'react'
import { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppService, DomainIntegrationService } from '../../services'
import {
    Background,
    BackgroundVariant,
    Controls,
    MiniMap,
    ReactFlow,
    ReactFlowProps,
    ReactFlowProvider,
} from '@xyflow/react'
import { LoadingOutlined } from '@ant-design/icons'
import { Drawer, Well } from '@pushly/aqe/lib/components'
import { CustomNode } from './node-types/custom-node/custom-node'
import { CustomEdge } from './edge-types/custom-edge/custom-edge'
import { SplitStepNode } from './node-types/split-step-node/split-step-node'
import { ConditionStepNode } from './node-types/condition-step-node/condition-step-node'
import { BranchEdge } from './edge-types/branch-edge/branch-edge'
import { NodeEditor } from './editors/node-editor'
import { JourneyBuilderMode } from './enums'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { DomainDto } from '../../dtos/domain'
import { JourneyContext } from './context'
import { useJourneyReducer } from './reducer'
import titleCase from 'title-case'
import { getClassNames } from '../../_utils/classnames'
import { buildInitialJourneyFlow, parseJourneyFromFlow, graphHasMeaningfulSteps } from './helpers'
import { buildDefaultSteps } from '@pushly/models/lib/structs/journeys'
import { getRandId } from '@pushly/models/lib/utils/get-rand-id'
import { validateSplitStepGraph } from './utils/graph/validation'
import { simpleNotification } from '../../_utils/utils'
import { getEditableState } from '../../components/journey-builder/helpers/journey'
import { JourneyEditableState } from '@pushly/models/lib/enums/journeys'
import { Journey } from './types/journey-api-response'
import { JourneyEdge, JourneyNode, NodeEditorRef, isActionJourneyNode, isActionStep, SendNotificationActionParams } from './types/journey-nodes'
import { CopyStates, JourneyAction } from './types/journey-context'
import { GetDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { getStepConfig, resolveLabel } from './step-config'
import { CatalogItemType } from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import './styles/journey.scss'
import { StatusType } from '../../enums/status-type'
import { RemoveStepConfirmationModal } from './modals/remove-step-confirmation-modal'
import { JourneyStatusRef } from './types/journey-nodes'
import { Prompt, useLocation } from 'react-router-dom'
import { ViewportAdjuster, ViewportAdjusterRef } from './viewport-adjuster'
import { useRenderedJourneyGraph } from './layout/use-rendered-journey-graph'

const MINIMAP_ZOOM_THRESHOLD = 0.85

type JourneyBuilderProps = {
    ref?: Ref<JourneyStatusRef>
    loading?: boolean
    domain: DomainDto
    journey: Journey
    domainIntegrations?: GetDomainIntegrationResponse[]
    onChange?: (journey: Journey) => void
    onSubmit?: (journey: Journey, remove?: boolean) => void
    mode?: JourneyBuilderMode
    flowOverrides?: ReactFlowProps<JourneyNode, JourneyEdge>
}

export const JourneysBuilder = forwardRef<JourneyStatusRef, JourneyBuilderProps>(
    ({ loading, domain, journey, domainIntegrations, mode, onChange, onSubmit, flowOverrides }, ref) => {
        // region Builder Definitions
        const appSvc = useService(AppService)
        const domainIntegrationSvc = useService(DomainIntegrationService)

        const loc = useLocation()
        const locSearchParams = new URLSearchParams(loc.search)

        const [loadingDependencies, setLoadingDependencies] = React.useState(false)
        const [removeModalOpen, setRemoveModalOpen] = React.useState(false)

        const [zoomLevel, setZoomLevel] = React.useState(1)

        const nodeRef = useRef<NodeEditorRef>(null)
        const builderRef = useRef<{ builderHasChanged: boolean }>({ builderHasChanged: false })
        const viewportAdjusterRef = useRef<ViewportAdjusterRef>(null)

        // Nodes are rendered from these types passed to ReactFlow StepTypes prop
        // node.type === StepTypes[node.type] determines render component
        const StepTypes = useMemo(
            () => ({
                custom: CustomNode,
                'split-step': SplitStepNode,
                'condition-step': ConditionStepNode,
            }),
            [],
        )

        // Edges are rendered the these types passed to ReactFlow.edgeTypes
        // edge.type === edgeTypes[node.type] determines render component
        const edgeTypes = useMemo(
            () => ({
                custom: CustomEdge,
                'branch-edge': BranchEdge,
            }),
            [],
        )
        // endregion

        // region Node Handlers
        // --------------- Node Interactions -------------------- //

        // local handlers

        const handleCloseEditor = () => {
            dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
        }

        const handlePaneClick = useCallback(() => {
            dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
        }, [])

        const handleMove = useCallback(() => {
            dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
        }, [])

        const handleViewportChange = useCallback((viewport: { zoom: number }) => {
            setZoomLevel(viewport.zoom)
        }, [])

        const handleSubmitJourney = () => {
            // Run split step graph validation before save
            const graphValidation = validateSplitStepGraph(state.nodes, state.edges)
            if (!graphValidation.valid) {
                const firstError = graphValidation.errors[0]
                simpleNotification('error', firstError?.message ?? 'Journey contains validation errors')
                return
            }

            const journeyUpdate = parseJourneyFromFlow(state)
            if (!journeyUpdate) return

            if (
                mode !== JourneyBuilderMode.CREATE &&
                // @todo NS-1663 - update to Journey types/helpers
                getEditableState(journey) === JourneyEditableState.RUNNING &&
                state.removedNodes.length > 0
            ) {
                setRemoveModalOpen(true)
            } else {
                onSubmit?.(journeyUpdate)
            }
        }

        // endregion

        // initialize journey context
        //
        // The journey from the API is already a plain object (no struct instances).
        // structuredClone produces a deep copy for the reducer's initial state.
        const plainJourney: Journey = structuredClone(journey)
        const [state, dispatch] = useJourneyReducer({
            domain,
            journey: plainJourney,
            mode: mode ?? JourneyBuilderMode.READONLY,
            nodes: [],
            edges: [],
            copy: { state: CopyStates.CLEAR, node: null },
            editorOpen: false,
        })

        const {
            renderNodes,
            renderEdges,
            onRenderNodesChange,
            onRenderEdgesChange,
            clearRenderGraph,
        } = useRenderedJourneyGraph({
            canonicalNodes: state.nodes,
            canonicalEdges: state.edges,
        })

        // Emit onChange from canonical reducer state.
        // Guard against initial empty state to avoid spurious emissions on mount.
        useEffect(() => {
            if (state.nodes.length > 0) {
                const updated = parseJourneyFromFlow(state)
                if (updated) onChange?.(updated)
            }
        }, [state.nodes, state.edges])

        // on mount build journey flow, dispatch to journey context initial states
        useEffect(() => {
            const getDomainIntegrations = async () => {
                setLoadingDependencies(true)
                const response = await domainIntegrationSvc.fetchAll(domain.id)
                let integrationMap: Map<number, GetDomainIntegrationResponse> = new Map()

                if (response.ok && response.data?.length) {
                    integrationMap = new Map(
                        response.data.map((int) => [int.id, GetDomainIntegrationResponse.from(int)]),
                    )
                    dispatch({
                        action: JourneyAction.SET_JOURNEY_INTEGRATIONS,
                        data: integrationMap,
                    })
                }

                setLoadingDependencies(false)
                return integrationMap
            }

            const buildInitialJourneyNodesAndEdges = async () => {
                clearRenderGraph()

                const integrations = domainIntegrations?.length
                    ? new Map(domainIntegrations.map((int) => [int.id, GetDomainIntegrationResponse.from(int)]))
                    : await getDomainIntegrations()

                if (journey) {
                    const steps = mode === JourneyBuilderMode.CREATE
                        // buildDefaultSteps returns class-based JourneyStep[] while Journey['steps']
                        // expects interface-based IJourneyStep[]. The double cast bridges this mismatch.
                        ? buildDefaultSteps(getRandId) as unknown as Journey['steps']
                        : journey.revision.steps

                    const [initialNodes, initialEdges] = buildInitialJourneyFlow(steps, {
                        integrations,
                    })

                    dispatch({ action: JourneyAction.SET_JOURNEY, data: journey })
                    dispatch({
                        action: JourneyAction.ON_JOURNEY_CHANGES,
                        data: { nodes: initialNodes, edges: initialEdges },
                    })

                    const initialNodeId = locSearchParams.get('step_id')
                    if (initialNodeId && initialNodeId.match(/^\d+$/)) {
                        const initialActiveNode = initialNodes.find((n) => String(n.id) === initialNodeId) ?? null
                        if (initialActiveNode) {
                            dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: initialActiveNode })
                        }
                    }
                }
            }

            buildInitialJourneyNodesAndEdges()
        }, [journey?.id])
        // endregion

        // region Snapshot-based dirty detection
        // Instead of per-node hash tracking (UIEditTrackable.hasChanged), we capture a
        // canonical snapshot of the step data once the flow is fully initialized (including
        // async notification enrichment in CustomNode). We compare against that snapshot
        // to determine if the user has made meaningful changes.

        const initialSnapshotRef = useRef<string | null>(null)
        const [snapshotReady, setSnapshotReady] = useState(false)
        const pendingEnrichmentsRef = useRef(0)

        // Called by CustomNode when it starts/finishes async notification fetching.
        // Once all enrichments complete, we capture the initial snapshot.
        const enrichmentCallbacks = useMemo(() => ({
            onEnrichStart: () => { pendingEnrichmentsRef.current++ },
            onEnrichEnd: () => {
                pendingEnrichmentsRef.current = Math.max(0, pendingEnrichmentsRef.current - 1)
                if (pendingEnrichmentsRef.current === 0 && !snapshotReady) {
                    setSnapshotReady(true)
                }
            },
        }), [snapshotReady])

        // Normalize steps for comparison: strip UI-only fields, sort keys
        const normalizeStepsForSnapshot = useCallback((nodes: JourneyNode[]): string => {
            const steps = nodes
                .filter(n => n.data.step.type !== StepType.GLOBAL_TRIGGER)
                .map(n => {
                    const { step } = n.data
                    // Only compare step-level data that matters for persistence:
                    // id, type, configuration (without UI enrichments like notification object for display)
                    if (isActionStep(step)) {
                        const params = step.configuration.params as SendNotificationActionParams
                        // Strip the full notification object (enrichment/display only)
                        // but include a template fingerprint so content edits are detected
                        const { notification, ...restParams } = params
                        const contentFingerprint = notification?.template
                            ? JSON.stringify(notification.template)
                            : undefined
                        return {
                            id: step.id,
                            type: step.type,
                            configuration: {
                                ...step.configuration,
                                params: { ...restParams, _tf: contentFingerprint },
                            },
                        }
                    }
                    return { id: step.id, type: step.type, configuration: step.configuration }
                })
                .sort((a, b) => String(a.id).localeCompare(String(b.id)))
            return JSON.stringify(steps)
        }, [])

        // Capture initial snapshot once all async enrichments are done
        useEffect(() => {
            if (!snapshotReady || initialSnapshotRef.current !== null) return
            // Small delay to ensure the final reducer state has settled
            const timer = setTimeout(() => {
                if (state.nodes.length > 0) {
                    initialSnapshotRef.current = normalizeStepsForSnapshot(state.nodes)
                }
            }, 50)
            return () => clearTimeout(timer)
        }, [snapshotReady, state.nodes])

        // If there are no action nodes that need enrichment, mark snapshot ready immediately
        useEffect(() => {
            if (state.nodes.length > 0 && pendingEnrichmentsRef.current === 0 && !snapshotReady) {
                // Check if any action nodes exist that would trigger enrichment
                const hasActionNodes = state.nodes.some(
                    n => isActionJourneyNode(n) && n.data.step.configuration?.params?.notification_id,
                )
                if (!hasActionNodes) {
                    setSnapshotReady(true)
                }
            }
        }, [state.nodes.length, snapshotReady])

        const detectChanges = useCallback(() => {
            // No snapshot yet — not dirty (avoids false positive on load)
            if (initialSnapshotRef.current === null) return false

            // In CREATE mode, a scaffolding-only graph is not meaningfully modified
            if (mode === JourneyBuilderMode.CREATE && !graphHasMeaningfulSteps(state.nodes)) {
                return false
            }

            const currentSnapshot = normalizeStepsForSnapshot(state.nodes)
            return currentSnapshot !== initialSnapshotRef.current
        }, [state.nodes, normalizeStepsForSnapshot, mode])

        // endregion

        useEffect(() => {
            if (state.journey?.id) {
                /** Track if builder has changed for submit button text and beforeunload warning */
                const changed = detectChanges()
                builderRef.current.builderHasChanged = changed
            }
        }, [state.nodes, JSON.stringify(state.journey), detectChanges])

        useImperativeHandle(ref, () => ({
            builderHasChanged: builderRef.current.builderHasChanged ?? false,
            markSaveComplete: () => {
                builderRef.current.builderHasChanged = false
                // Reset snapshot to current state so future comparisons start clean
                initialSnapshotRef.current = normalizeStepsForSnapshot(state.nodes)
            },
        }))

        // Warn user before closing/navigating away with unsaved changes
        useEffect(() => {
            if (mode === JourneyBuilderMode.READONLY) return

            const handler = (e: BeforeUnloadEvent) => {
                if (builderRef.current.builderHasChanged) {
                    e.preventDefault()
                }
            }

            window.addEventListener('beforeunload', handler)
            return () => window.removeEventListener('beforeunload', handler)
        }, [mode])

        return (
            <Well
                className={getClassNames('journey-builder-well', 'nested')}
                title="Builder"
                onSubmit={handleSubmitJourney}
                submitText={builderRef.current?.builderHasChanged ? 'Save Changes' : 'Save'}
                hideSubmit={mode === JourneyBuilderMode.READONLY}
                hideCancel={true}
                loading={false}
            >
                <Prompt
                    when={mode !== JourneyBuilderMode.READONLY}
                    message={() => {
                        if (builderRef.current?.builderHasChanged) {
                            return 'You have unsaved changes. Are you sure you want to leave?'
                        }
                        return true
                    }}
                />
                {(loading || loadingDependencies) ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 400 }}>
                        <LoadingOutlined spin style={{ fontSize: 32, color: '#75808a' }} />
                    </div>
                ) : state.journey && (
                    <JourneyContext.Provider value={{ state, dispatch, enrichment: enrichmentCallbacks }}>
                        <ReactFlowProvider>
                            <ViewportAdjuster ref={viewportAdjusterRef} nodes={renderNodes} edges={renderEdges} />
                            <ReactFlow<JourneyNode, JourneyEdge>
                                className={getClassNames('journey-builder')}
                                nodes={renderNodes}
                                edges={renderEdges}
                                nodeTypes={StepTypes}
                                onNodesChange={onRenderNodesChange}
                                nodesConnectable={false}
                                nodesDraggable={false}
                                nodesFocusable={mode !== JourneyBuilderMode.READONLY}
                                edgeTypes={edgeTypes}
                                onEdgesChange={onRenderEdgesChange}
                                edgesReconnectable={false}
                                edgesFocusable={false}
                                onPaneClick={handlePaneClick}
                                onMove={handleMove}
                                onViewportChange={handleViewportChange}
                                {...flowOverrides}
                                selectionOnDrag={false}
                                selectionKeyCode={null}
                                maxZoom={1}
                                zoomOnDoubleClick={false}
                                proOptions={{
                                    hideAttribution: true,
                                }}
                            >
                                <Background
                                    variant={BackgroundVariant.Lines}
                                    bgColor="rgb(242, 242, 242)"
                                    color="rgb(217, 217, 217)"
                                    lineWidth={0.5}
                                    gap={15}
                                />
                                <Controls
                                    position="top-right"
                                    orientation="horizontal"
                                    showInteractive={false}
                                    showFitView={false}
                                >
                                    <button
                                        type="button"
                                        className="react-flow__controls-button react-flow__controls-fitview"
                                        title="Fit View"
                                        aria-label="Fit View"
                                        onClick={() => viewportAdjusterRef.current?.fitView()}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 30">
                                            <path d="M3.692 4.63c0-.53.4-.938.939-.938h5.215V0H4.708C2.13 0 0 2.054 0 4.63v5.216h3.692V4.631zM27.354 0h-5.2v3.692h5.17c.53 0 .984.4.984.939v5.215H32V4.631A4.624 4.624 0 0027.354 0zm.954 24.83c0 .532-.4.94-.939.94h-5.215v3.768h5.215c2.577 0 4.631-2.13 4.631-4.707v-5.139h-3.692v5.139zm-23.677.94c-.531 0-.939-.4-.939-.94v-5.138H0v5.139c0 2.577 2.13 4.707 4.708 4.707h5.138V25.77H4.631z" />
                                        </svg>
                                    </button>
                                </Controls>
                                {zoomLevel < MINIMAP_ZOOM_THRESHOLD && (
                                    <MiniMap
                                        position="bottom-right"
                                        pannable
                                        zoomable={false}
                                        nodeStrokeWidth={3}
                                        style={{ opacity: Math.min(1, (MINIMAP_ZOOM_THRESHOLD - zoomLevel) / 0.2) }}
                                    />
                                )}
                            </ReactFlow>
                        </ReactFlowProvider>

                        <Drawer
                            getContainer={appSvc.getAppContainer}
                            className={getClassNames(
                                'journey-node-editor',
                                state.activeNode?.data?.step?.type,
                                state.activeNode?.data?.step?.configuration?.type,
                            )}
                            destroyOnClose={true}
                            title={`${titleCase(
                                state.mode === JourneyBuilderMode.READONLY ? 'View' : state.mode,
                            )} ${(() => {
                                const node = state.activeNode
                                if (!node) return ''
                                const config = getStepConfig(node.data.step)
                                if (!config || config.kind !== 'user') return ''
                                return resolveLabel(config, state.domain.ecommConfig?.itemType ?? CatalogItemType.PRODUCT)
                            })()}`}
                            placement="right"
                            closable={true}
                            maskClosable={false}
                            open={state.editorOpen}
                            onClose={handleCloseEditor}
                            hideFooter={mode === JourneyBuilderMode.READONLY}
                            submitText="Update Step"
                            onSubmit={async () => {
                                const result = await nodeRef?.current?.onSubmit()

                                if (result) {
                                    dispatch({ action: JourneyAction.ON_NODE_STEP_CHANGE, data: result })
                                    if (mode !== JourneyBuilderMode.READONLY) {
                                        simpleNotification('warning', "Step updated. Don't forget to save your journey.")
                                    }
                                }

                                // Always close the drawer, even if no changes were made
                                dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
                            }}
                            footer={state.editorFooter}
                        >
                            {state.activeNode && <NodeEditor node={state.activeNode!} ref={nodeRef} />}
                        </Drawer>

                        <RemoveStepConfirmationModal
                            changeSet={{ campaign: parseJourneyFromFlow(state) }}
                            visible={removeModalOpen}
                            onConfirm={(updatedJourney, removeSubs) => {
                                setRemoveModalOpen(false)
                                onSubmit?.(updatedJourney, removeSubs)
                            }}
                            onCancel={() => setRemoveModalOpen(false)}
                        />
                    </JourneyContext.Provider>
                )}
            </Well>
        )
    },
)
